/**
 * @file `<NextSteps/>` — bridge component a closing chapter uses to
 *       recap the Part and hand the learner to what's next.
 * @module @labcraft/course-kit/components/next-steps
 *
 * Renders children as the recap body, plus a CTA link. The CTA is an
 * anchor (not a button) so right-click / open-in-new-tab / browser
 * back behave correctly. Under the all-free access policy the closing
 * bridges to the next Part / the course catalog — not a paid-course
 * upsell — so the default `href` is `/courses` (the learner's course
 * list). Authors override `href` + `ctaLabel` per closing chapter to
 * point at the specific next Part.
 */

import type { JSX, ReactNode } from 'react'
import { ChevronRightIcon } from './icons.tsx'
import styles from './next_steps.module.css'

export interface NextStepsProps {
  /** Recap / bridge prose shown above the CTA. */
  children: ReactNode
  /** Override the default CTA label (defaults to `Continue`). */
  ctaLabel?: string
  /**
   * Override the CTA target. Defaults to `/courses` — the learner's
   * course list, where they pick the next Part. Authors point this at
   * the specific next Part per closing chapter.
   */
  href?: string
}

export function NextSteps(props: NextStepsProps): JSX.Element {
  const label = props.ctaLabel ?? 'Continue'
  const href = props.href ?? '/courses'
  return (
    <aside className={styles.next} aria-label="What's next">
      <div className={styles.body}>{props.children}</div>
      <a className={styles.cta} href={href}>
        <span>{label}</span>
        <ChevronRightIcon />
      </a>
    </aside>
  )
}
